import { FC } from "react";
import { HoverTooltip } from "@app/dashboard/src/components/shared/HoverTooltip";
import { Icons } from "@app/dashboard/src/components/shared";

/**
 * Info tooltip for Offline Mode, shared so the read-only device summary and the editable checkbox
 * can't drift apart.
 *
 * Styling deliberately mirrors the Firmware Version tooltip in DeviceConfigTabView — same
 * `ml-2 text-brand-primary cursor-help` icon, same plain-text body, same default placement — so the
 * two read as one UI rather than two.
 *
 * On the setup guide: "Omnisight FusionSensor Local Configuration Set-up Guide" (02-Oct-2025) covers
 * reaching and configuring a device locally, and never mentions Offline Mode; the two are
 * independent, since that flow works whether or not this is on. So the wording below comes from
 * behaviour in the code, not from a spec:
 *   - ConfigurationModule: "the heartbeat loop and SyncEngine TransferGate both hold all outbound
 *     API calls", and OfflineModePollInterval re-reads settings every 15 minutes, with the
 *     SettingsChanged chain resuming normal operation — so no restart, unlike the guide's Step 5.
 *   - MediaRequestTransitions.ShouldUpload suppresses media uploads.
 *   - DeviceSettings.DataRetentionDays: 0/null means "keep until sent to the server".
 */
export const OfflineModeInfo: FC = () => (
    <HoverTooltip hoverable={<Icons.Info className="ml-2 text-brand-primary cursor-help" />}>
        Runs the device standalone — heartbeats, data sync and media uploads are all held, so nothing leaves the device.
        Set a Data Retention limit, or records are kept until they reach the server, which never happens while offline.
        The device re-checks the server every 15 minutes, so turning this off from the Dashboard recovers it without a restart.
    </HoverTooltip>
);
